<script setup>
    import { computed, ref, inject, defineEmits } from "vue";
    import { useMutation, useQueryClient, useQuery } from "@tanstack/vue-query";
    import { ElButton, ElInput, ElSwitch } from "element-plus";
    import axios from "axios";
    import iressLogo from "../../assets/images/iressLogo.png";

    const userService = inject('UserService');

    const queryClient = useQueryClient();

    const isAdmin = computed(() => !!userService.getUserDetails()?.isAdminUser);

    const { data: currentIntegration, isLoading: isLoadingCurrent } = useQuery({
        queryKey: ["iress-integration"],
        queryFn: () =>
            axios("api/Integration/GetIressIntegration").then((res) => res.data),
    });

    const { mutate: deleteUserIntegration, isLoading: isDeleting } = useMutation({
        mutationFn: () =>
            axios.delete("api/Integration/DeleteUserIntegration/IRESS"),
        onSuccess: () => {
            iressCredentials.value.userName = "";
            iressCredentials.value.password = "";
            iressCredentials.value.userId = "";
            queryClient.invalidateQueries(["iress-integration"]);
            queryClient.invalidateQueries(["user-integrations"]);
            userService.update();
        },
    });

    const iressCredentials = ref({
        userName: "",
        password: "",
        userId: "",
        isPreview: false,
    });

    const emit = defineEmits(["saveCredentials"]);

    const { mutate: saveCredentials, isLoading: isSaving, isError } = useMutation({
        mutationFn: () =>
            axios.post("api/Integration/SaveIressCredentials", iressCredentials.value),
        onSuccess: async () => {
            queryClient.invalidateQueries(["iress-integration"]);
            queryClient.invalidateQueries(["user-integrations"]);
            await userService.update();
            emit("saveCredentials", "IRESS");
        }
    });

    const isLoading = computed(
        () => isSaving.value || isLoadingCurrent.value || isDeleting.value
    );
    const complete = computed(() =>
        !!iressCredentials.value.userName.trim() &&
        !!iressCredentials.value.password &&
        !!iressCredentials.value.userId.trim()
    );
    const disabled = computed(() => !!currentIntegration.value || isLoading.value || !complete.value);

    const connectivity = ref(null);
    const { mutate: checkConnectivity, isLoading: isChecking } = useMutation({
        mutationFn: () => axios.post("api/Integration/IressConnectivityCheck").then((res) => res.data),
        onSuccess: (result) => { connectivity.value = result; },
        onError: (error) => {
            connectivity.value = { ok: false, error: error?.response?.data?.message || error.message };
        },
    });
</script>

<template>
    <div class="integration-section" style="background-color: #dff0ea;">
        <div class="right-section">
            <div class="description">
                <p class="mb-2 font-same">
                    To obtain quotes from Iress The Exchange, please provide the <b>username</b>, <b>password</b> and <b>user id</b> of your Exchange account.
                </p>
                <p class="mb-2 font-same">
                    Quotes are returned for the providers and products enabled on your Exchange account.
                </p>
                <p class="mb-2 font-style">
                    You only need to provide these details once, and we will not ask for them again.
                </p>
            </div>
            <div class="form-section">
                <div class="form-card">
                    <div class="image-logo">
                        <img :src="iressLogo" alt="Iress" />
                    </div>
                    <div class="form-group">
                        <div class="form-row">
                            <label for="IressUserName">Username</label>
                        </div>
                        <ElInput v-if="!!currentIntegration" id="IressUserName" v-model="currentIntegration.userName" disabled />
                        <ElInput v-else id="IressUserName" v-model="iressCredentials.userName" :min-length="1" type="text" autofocus :class="{ error : isError}" />
                    </div>

                    <div class="form-group">
                        <div class="form-row">
                            <label for="IressPassword">Password</label>
                        </div>
                        <ElInput v-if="!!currentIntegration" id="IressPassword" disabled value="xxxxxxxxxxxxx" type="password" />
                        <ElInput v-else id="IressPassword" v-model="iressCredentials.password" :min-length="1" type="password" />
                    </div>

                    <div class="form-group">
                        <div class="form-row">
                            <label for="IressUserId">User id</label>
                        </div>
                        <ElInput v-if="!!currentIntegration" id="IressUserId" v-model="currentIntegration.exchangeUserId" disabled />
                        <ElInput v-else id="IressUserId" v-model="iressCredentials.userId" :min-length="1" type="text" :class="{ error : isError}" />
                        <span v-if="!currentIntegration" class="hintText">Iress allocates this alongside your Exchange account. If you do not know it, ask Iress or contact us and we will request it for you.</span>
                    </div>

                    <div class="form-group" v-if="isError">
                        <span class="errorText">Sorry, we were unable to link this Iress account, please check the details and try again.</span>
                    </div>

                    <div class="form-group" v-if="isAdmin">
                        <div class="ext">
                            <label for="isPreview">IFA Preview (test) environment <b>(Admin only)</b></label>
                            <ElSwitch id="isPreview" v-model="iressCredentials.isPreview" :disabled="!!currentIntegration" />
                        </div>
                    </div>

                    <ElButton v-if="!!currentIntegration" type="danger" @click="deleteUserIntegration" :loading="isLoading">
                        Unlink account
                    </ElButton>

                    <ElButton v-else type="primary" @click="saveCredentials" :loading="isLoading" :disabled="disabled">
                        Link account
                    </ElButton>

                    <div v-if="!!currentIntegration && isAdmin" class="connectivity">
                        <ElButton type="info" plain @click="checkConnectivity" :loading="isChecking">
                            Test connection <b>(Admin only)</b>
                        </ElButton>
                        <div v-if="connectivity" :class="['connectivity-result', connectivity.ok ? 'ok' : 'failed']">
                            <template v-if="connectivity.ok">
                                <p><b>The Exchange answered</b> ({{ connectivity.environment }}) in {{ connectivity.elapsedMilliseconds }} ms.</p>
                                <p>{{ connectivity.quoteCount }} quotes, {{ connectivity.pricedCount }} priced, from {{ connectivity.providers.length }} providers.</p>
                                <p v-if="connectivity.providers.length" class="providers">{{ connectivity.providers.join(', ') }}</p>
                                <p class="meta">Message {{ connectivity.messageId }}<span v-if="connectivity.rawMessagesLogged"> · request and response written to the raw message log</span></p>
                            </template>
                            <template v-else>
                                <p><b>The check failed.</b></p>
                                <p>{{ connectivity.error }}</p>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .errorText {
        color: red;
        font-size: small;
    }

    .hintText {
        color: #6b6b6b;
        font-size: small;
    }

    .form-section {
        width: 350px;
        background-color: #ffffff;
        padding: 10px;
    }

    .form-group {
        margin-bottom: 1rem;
    }

    .form-card {
        background-color: #ffffff;
        padding: 15px;
        box-shadow: 0 2px 12px 0 rgba(0,0,0,.1);
    }

    .form-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.25rem;
    }

        .form-row label {
            font-size: 0.875rem;
        }

    .right-section {
        flex: 1;
        display: flex;
    }

        .right-section .description {
            flex: 1;
            padding-right: 2rem;
            font-size: 0.875rem;
        }

    .image-logo {
        background: white;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        margin-bottom: 10px;
    }

        .image-logo img {
            max-height: 48px;
            width: auto;
            object-fit: contain;
            margin-bottom: 5px;
        }

    .ext {
        display: flex;
        width: 100%;
        justify-content: space-between;
        align-items: center;
    }

    .connectivity {
        margin-top: 1rem;
        font-size: 0.8rem;
    }

    .connectivity-result {
        margin-top: 0.5rem;
        padding: 0.5rem 0.75rem;
        border-radius: 4px;
        border: 1px solid #c9d6d1;
        background-color: #f4faf7;
    }

        .connectivity-result.failed {
            border-color: #e8b4b4;
            background-color: #fdf3f3;
        }

        .connectivity-result p {
            margin: 0 0 0.25rem 0;
        }

        .connectivity-result .providers {
            color: #3c3c3c;
        }

        .connectivity-result .meta {
            color: #6b6b6b;
            font-size: 0.7rem;
            word-break: break-all;
        }
</style>
